import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { serverUrl } from '../config';
import { routes, WriteToMeParameters } from '../data/routes';
import ShortUuids from '../lib/ShortUuids';
import { colors } from '../Stitches';
import { Authenticated } from './AuthGuards';
import Emoji from './Emoji';
import { useAuthenticateApiCall } from './hooks/useAuthenticateApiCall';
import { useInvitations } from './hooks/useInvitations';
import { useAtomValue } from 'jotai';
import { sessionAtom } from '../lib/Session';

const fetchInvitationDetails = (token: string) => (invitationId: string) => () => {
  const fetchUrl = `${serverUrl}/api/invitations/${invitationId}`;
  const request = {
    method: 'get',
    headers: { authorization: `Token ${token}` },
  };

  return fetch(fetchUrl, request).then((res) => res.json());
};

const useAcceptDismissInvitation = (onClose: any) => {
  const { dispatch } = useInvitations();

  const onAccept = useCallback(() => {
    dispatch({ type: 'acceptInvitation' });
    onClose();
  }, [dispatch, onClose]);

  const onDismiss = useCallback(() => {
    dispatch({ type: 'dismissInvitation' });
    onClose();
  }, [dispatch, onClose]);

  return { onAccept, onDismiss };
};

const useOnInvitationOpen = (onOpen: any, onClose: any) => {
  const { invitationId } = useInvitations();

  useEffect(() => {
    if (invitationId) {
      onOpen();
    } else {
      onClose();
    }
  }, [invitationId, onOpen, onClose]);
};

type AcceptInvitationModalProps = { data: { id: string; firstName: string; lastName: string } };
const AcceptInvitationModal = ({ data }: AcceptInvitationModalProps) => {
  const { onOpen, isOpen, onClose } = useDisclosure();

  const { onAccept, onDismiss } = useAcceptDismissInvitation(onClose);
  const backgroundColor = useColorModeValue(undefined, colors.dark.whiteAlpha100);

  useOnInvitationOpen(onOpen, onClose);

  return (
    <Modal isOpen={isOpen} onClose={onDismiss} motionPreset="slideInBottom">
      <ModalOverlay />
      <ModalContent backgroundColor={backgroundColor}>
        <ModalHeader>Invitation</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>
            You have been invited to write to: {data.firstName} {data.lastName}
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="purple" mr={3} onClick={onAccept}>
            Accept Invitation
          </Button>
          <Button variant="ghost" onClick={onDismiss}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const WelcomeToLetterDeskModal = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { dispatch } = useInvitations();

  const closeModalClearInvitation = () => {
    dispatch({ type: 'dismissInvitation' });
    onClose();
  };

  useOnInvitationOpen(onOpen, onClose);

  return (
    <Modal isOpen={isOpen} onClose={closeModalClearInvitation} motionPreset="slideInBottom">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Welcome to LetterDesk!</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          I{"'"}m stoked to have you here in early access{' '}
          <Emoji symbol="🤠" label="Emoji of a cowboy" />{' '}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="purple" mr={3} onClick={closeModalClearInvitation}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const InvitationModal = ({ invitationId }: { invitationId: string }) => {
  const authedFetchInvitationDetails = useAuthenticateApiCall(fetchInvitationDetails);
  const { data, isLoading } = useQuery(
    ['invitation-details'],
    authedFetchInvitationDetails(invitationId)
  );

  return (
    <>
      {!isLoading &&
        (data.type === 'system' ? (
          <WelcomeToLetterDeskModal />
        ) : (
          <AcceptInvitationModal data={data} />
        ))}
    </>
  );
};

export const InvitationManager = () => {
  const { invitationId } = useInvitations();

  return (
    <Authenticated noRedirect>
      {invitationId && <InvitationModal invitationId={invitationId} />}
    </Authenticated>
  );
};

export const WriteToMe = () => {
  const { invitationId } = useParams<WriteToMeParameters>() as WriteToMeParameters;

  const { dispatch } = useInvitations();

  const longId = invitationId.length === 36 ? invitationId : ShortUuids.decode(invitationId);
  const toast = useToast();
  const navigate = useNavigate();
  const session = useAtomValue(sessionAtom);

  useEffect(() => {
    if (!longId) return;

    dispatch({ type: 'setInvitation', invitationId: longId });

    if (session) {
      navigate(routes.root);
    } else {
      toast({
        title: 'Please log in or register',
        description: 'to accept the invitation',
        position: 'top',
      });
      navigate(routes.register);
    }
  }, [toast, navigate, longId, dispatch, session]);

  return null;
};
